import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Chip,
  DataListItem,
  Box as Description,
  Box as Form,
  Input2FA,
  Box as Wrapper,
} from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useAddApiKeyMutation } from 'hooks/mutations'
import { ApiKeyCreateResponse, ApiKeyRequestForm } from 'types/apiKeys'
import { BaseErrorResponse } from 'types/beneficiaries'

type ApiKey2FAModalProps = {
  setShowSuccessModal: (response: ApiKeyCreateResponse) => void
  payload: ApiKeyRequestForm
}

export const ApiKey2FAConfirmation = ({ setShowSuccessModal, payload }: ApiKey2FAModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { profile } = useProfileContext()

  const [otc, setOtc] = useState('')
  const [errorOtpText, setErrorOtpText] = useState('')
  const [requestError, setRequestError] = useState('')
  const { mutate, isPending } = useAddApiKeyMutation()

  const getRoles = (payload: ApiKeyRequestForm) => {
    if (payload?.readOnly) {
      return profile.roles.filter(role => role.endsWith('_READ'))
    }

    const roles = new Set(profile.roles)

    if (payload?.withdrawals) {
      roles.add('ROLE_WITHDRAWAL')
    }

    if (payload?.payroll) {
      roles.add('ROLE_MPS_PAYROLL')
    }

    return Array.from(roles)
  }

  const mappedReq = (payload: ApiKeyRequestForm, otc: string) => {
    const allowedIps = payload?.allowedIps
      .filter(({ value }) => value !== '')
      .map(({ value }) => value)

    return {
      description: payload?.description,
      totp: otc,
      roles: getRoles(payload),
      allowedIps: allowedIps?.length ? allowedIps?.join(',') : null,
    }
  }

  const onSuccess = async () => {
    mutate(mappedReq(payload, otc), {
      onSuccess: resp => {
        queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
        setShowSuccessModal(resp)
        setErrorOtpText('')
      },
      onError: (error: BaseErrorResponse) => {
        setErrorOtpText(t('incorrectOtpCode'))
        if (error?.status === 400) {
          setRequestError(t('manageApiKeys.invalidRequest'))
        } else if (error.status !== 403) {
          logSentryError(
            'Error from ApiKey2FAModal V2 - createApiKey',
            new Error(JSON.stringify(error?.data?.errorList))
          )
          setErrorOtpText(t('oopsSomethingWentWrong'))
        } else {
          setRequestError(t('oopsSomethingWentWrong'))
        }
      },
    })
  }

  useEffect(() => {
    if (otc?.length === TWO_FA_REQUIRED_LENGTH) {
      onSuccess()
    }
  }, [otc])

  const getAdditionallyEnabledFeatures = () => {
    const features: string[] = []

    if (payload?.readOnly) {
      features.push(t('manageApiKeys.redOnly'))
    } else if (payload?.withdrawals) {
      features.push(t('manageApiKeys.enableWithdrawalsLabel'))
    }

    if (features?.length) {
      return features?.length > 1 ? features?.join(', ') : features?.toString()
    } else {
      return '-'
    }
  }

  return (
    <Wrapper paddingT={24} gapY={24} flex flexCol className="max-w-[608px] w-full mx-auto">
      <Description flex flexCol borderRadius={4} className="w-full">
        <Box key={payload?.description} flexCol>
          <DataListItem
            id="api-key-name"
            testId="api-key-name"
            title={t('name')}
            body={payload?.description}
            layout="horizontal"
            size="large"
          />

          <Box flex justify="between" className="py-2 min-h-10">
            <Box className="text-foreground-subdued text-body-regular-large">
              {t('manageApiKeys.allowedIpAddressInputLabel')}
            </Box>
            <Box flex isFlexWrap gap={8} justify="end" className="max-w-[70%]">
              {payload?.allowedIps?.map(ip => (
                <Chip
                  key={ip?.label}
                  label={ip?.label}
                  name={ip?.label}
                  dismissible={false}
                  variant="input"
                  isSelected={false}
                />
              ))}
            </Box>
          </Box>

          <DataListItem
            id="additional-features"
            testId="additional-features"
            title={t('manageApiKeys.additionallyEnabledFor')}
            body={getAdditionallyEnabledFeatures()}
            layout="horizontal"
            size="large"
          />
        </Box>
      </Description>

      <Form>
        <Box className="text-body-strong text-foreground-prominent">{t('authCodeConfirm')}</Box>
        <Input2FA
          testId="apikey-2fa"
          loading={isPending}
          onComplete={value => {
            setOtc(value)
          }}
          error={errorOtpText}
        />
      </Form>
      {requestError && <Box color="error">{requestError}</Box>}
    </Wrapper>
  )
}
